import React, {useEffect, useState} from 'react'
import {ArcElement, Chart, Legend, Tooltip} from 'chart.js'
import {Doughnut} from 'react-chartjs-2'
import Moment from 'react-moment'
import SweetAlert from 'react-bootstrap-sweetalert'
import {ProjectGuestPortalClient} from '../../infrastracture/api/ProjectGuestPortalClient'
import {ProjectGuestBookAssetModal} from './ProjectGuestBookAssetModal'
import {GuestTheme, useProjectGuest} from './ProjectGuestContext'

Chart.register(ArcElement, Tooltip, Legend)

// Each action is drawn in its own day-state colour when selected, the way the Teams client
// does it: on site and off site never share a colour. Not working has no button of its own here
// (the card only offers the two), but keeps its theme colour for the roster doughnut below.
const ACTIONS = [
  {
    state: 'workFromOffice',
    icon: 'fa-duotone fa-solid fa-building',
    label: 'On site',
    color: (theme: GuestTheme) => theme.onSite,
  },
  {
    state: 'workFromHome',
    icon: 'fa-duotone fa-solid fa-house',
    label: 'Off site',
    color: (theme: GuestTheme) => theme.home,
  },
] as const

// The week view always returns Monday-first, so Saturday and Sunday are the last two.
const WEEKEND_INDEXES = [5, 6]

type Reason = {id: string; name: string}
type ProjectSite = {siteId: string; siteName: string}

function mondayOf(date: Date) {
  const d = new Date(date)
  d.setHours(0, 0, 0, 0)
  // getDay() is 0 for Sunday, so shift it into a Monday-first week before subtracting.
  d.setDate(d.getDate() - ((d.getDay() + 6) % 7))
  return d
}

function toDateOnly(d: Date) {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

// Compare on the calendar date as it was serialised rather than on a parsed Date: the week
// view's days are local and a project's start/end are UTC midnights, so parsing both would
// shift one of them across a day boundary for anyone west of UTC. YYYY-MM-DD strings sort
// correctly, which is all these comparisons need.
function dateKey(value: any) {
  if (typeof value === 'string' && value.length >= 10) return value.slice(0, 10)

  const parsed = new Date(value)
  return isNaN(parsed.getTime()) ? '' : toDateOnly(parsed)
}

function DayDoughnut({day, theme}: {day: any; theme: GuestTheme}) {
  const values = [
    day.workingOnSite || 0,
    day.workingFromHome || 0,
    day.notWorking || 0,
    day.noResponse || 0,
  ]
  const hasData = values.some((v) => v > 0)

  const percentOnSite =
    day.total > 0 ? Math.round(((day.workingOnSite || 0) / day.total) * 100) : 0

  return (
    <div className='position-relative' style={{width: 64, height: 64}}>
      <Doughnut
        data={{
          labels: ['On site', 'Off site', 'Not working', 'No response'],
          datasets: [
            {
              data: hasData ? values : [0, 0, 0, 1],
              backgroundColor: hasData
                ? [theme.onSite, theme.home, theme.notWorking, theme.noResponse]
                : ['#f4f4f7'],
              borderWidth: 0,
            },
          ],
        }}
        options={{
          cutout: '72%',
          plugins: {legend: {display: false}, tooltip: {enabled: false}},
          maintainAspectRatio: false,
          responsive: true,
        }}
      />
      <div
        className='position-absolute top-50 start-50 translate-middle fw-bold'
        style={{fontSize: 11, color: '#3f4254'}}
      >
        {percentOnSite}%
      </div>
    </div>
  )
}

export function ProjectGuestWeekPanel({project}: {project: any}) {
  const {theme, assetTypeName} = useProjectGuest()
  const bookableName = assetTypeName || 'Asset'

  const [weekStart, setWeekStart] = useState(() => mondayOf(new Date()))
  const [days, setDays] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState<string>('')
  const [error, setError] = useState('')
  const [offSiteReasons, setOffSiteReasons] = useState<Reason[]>([])
  const [notWorkingReasons, setNotWorkingReasons] = useState<Reason[]>([])
  // Which day card is showing a picker, and for which action. On-site picks a site, the other
  // two pick a reason, one picker slot per card either way.
  const [picker, setPicker] = useState<{date: string; state: string} | null>(null)
  // Saturday and Sunday are hidden until asked for; most projects never run on them.
  const [showWeekend, setShowWeekend] = useState(false)
  // The day whose on-site roster is open below the cards, opened by clicking its doughnut.
  const [rosterDate, setRosterDate] = useState<string | null>(null)
  const [roster, setRoster] = useState<any[]>([])
  const [rosterLoading, setRosterLoading] = useState(false)
  // The booking waiting on the release confirmation, carrying the day it belongs to so the
  // card can show its saving state while the release runs.
  const [toRelease, setToRelease] = useState<{date: string; booking: any} | null>(null)
  // The day whose booking modal is open, if any.
  const [bookingDate, setBookingDate] = useState<string | null>(null)

  // Sites come from the hydrated project (GET /projects/{id}), so the picker only ever offers
  // sites the project actually runs at; the API rejects anything else regardless.
  const projectSites: ProjectSite[] = (project.sites || []).filter((s: any) => s.siteId)

  const load = async () => {
    setLoading(true)
    setError('')

    const response = await ProjectGuestPortalClient.GetWeekView(
      project.id,
      toDateOnly(weekStart)
    )

    if (response.successful) setDays(response.data || [])
    else setError('Failed to load your week.')

    setLoading(false)
  }

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project.id, weekStart])

  useEffect(() => {
    let cancelled = false

    ProjectGuestPortalClient.GetPlanReasons(project.id).then((response) => {
      if (cancelled || !response.successful) return
      setOffSiteReasons(response.data?.offSiteReasons || [])
      setNotWorkingReasons(response.data?.notWorkingReasons || [])
    })

    return () => {
      cancelled = true
    }
  }, [project.id])

  // Reloaded whenever the open day changes, and again after a save (which replaces `days`), so
  // the roster cannot sit showing a state the cards have already moved on from.
  useEffect(() => {
    if (!rosterDate) {
      setRoster([])
      return
    }

    let cancelled = false
    setRosterLoading(true)

    ProjectGuestPortalClient.GetDayOnSiteUsers(project.id, rosterDate).then((response) => {
      if (cancelled) return
      setRoster(response.successful ? response.data || [] : [])
      setRosterLoading(false)
    })

    return () => {
      cancelled = true
    }
  }, [project.id, rosterDate, days])

  const save = async (
    date: string,
    state: string,
    reasonId?: string | null,
    siteId?: string | null
  ) => {
    setSaving(date)
    setError('')
    setPicker(null)

    const response = await ProjectGuestPortalClient.SetDayPlan(
      project.id,
      date,
      state,
      state === 'workFromHome' ? reasonId : null,
      state === 'notWorking' ? reasonId : null,
      state === 'workFromOffice' ? siteId : null
    )

    setSaving('')

    if (!response.successful) {
      setError(response.data?.message || 'Could not save that day.')
      return
    }

    await load()
  }

  // Undoes a day the way the Teams client does: the server releases whatever was booked out
  // of the project's pool for that date and then removes the plan itself.
  const clearPlan = async (date: string) => {
    setSaving(date)
    setError('')
    setPicker(null)

    const response = await ProjectGuestPortalClient.ClearDayPlan(project.id, date)

    setSaving('')

    if (!response.successful) {
      setError(response.data?.message || 'Could not clear that day.')
      return
    }

    await load()
  }

  const releaseBooking = async () => {
    if (!toRelease) return

    const {date, booking} = toRelease
    setToRelease(null)
    setSaving(date)
    setError('')
 
    const isDelete = date >= todayKey

    const response = await ProjectGuestPortalClient.ReleaseCheckIn(project.id, booking.id, isDelete)

    setSaving('')

    if (!response.successful) {
      setError(
        response.validationErrors?.[0]?.message ||
          response.data?.message ||
          'Could not release that booking.'
      )
      return
    }

    await load()
  }

  // Picking a state costs one click. Off-site and not-working still ask for a reason when
  // there is a genuine choice to make: none saves straight through, one applies itself, more
  // than one opens the picker. Every detail stays editable from the card afterwards.
  const handleAction = (day: any, state: string) => {
    const date = toDateOnly(new Date(day.date))

    // Re-clicking the chosen action undoes the day, as it does in Teams — today included, since
    // ClearProjectGuestDayPlanCommand releases any active check-in before removing the plan.
    if (day.myState === state) {
      clearPlan(date)
      return
    }

    // On-site never asks first: saving with no site lets the API resolve the guest's own
    // default site, and the card's dropdown is there to move them afterwards.
    if (state === 'workFromOffice') {
      save(date, state)
      return
    }

    const reasons = state === 'workFromHome' ? offSiteReasons : notWorkingReasons

    if (reasons.length === 0) {
      save(date, state)
      return
    }

    if (reasons.length === 1) {
      save(date, state, reasons[0].id)
      return
    }

    setPicker({date, state})
  }

  const shiftWeek = (weeks: number) => {
    const next = new Date(weekStart)
    next.setDate(next.getDate() + weeks * 7)
    setWeekStart(next)
    // The open roster, picker and booking modal all belong to a day that is about to leave
    // the view.
    setRosterDate(null)
    setPicker(null)
    setBookingDate(null)
  }

  const todayKey = toDateOnly(new Date())
  const projectStartKey = dateKey(project.startDate)
  const projectEndKey = dateKey(project.endDate)

  const visibleDays = days.filter((_, index) => showWeekend || !WEEKEND_INDEXES.includes(index))
  const rosterDay = days.find((d: any) => toDateOnly(new Date(d.date)) === rosterDate)

  return (
    <div>
      <SweetAlert
        warning
        title='Release booking'
        show={!!toRelease}
        onConfirm={releaseBooking}
        onCancel={() => setToRelease(null)}
        confirmBtnText='Release'
        cancelBtnBsStyle='light'
        showCancel
        showConfirm
        showCloseButton
      >
        <span>Are you sure you want to release {toRelease?.booking?.assetName}?</span>
      </SweetAlert>

      <div className='d-flex flex-wrap align-items-center justify-content-center justify-content-md-between gap-3 mb-5'>
        <div className='d-flex align-items-center gap-2'>
          <button
            type='button'
            className='btn btn-sm btn-icon btn-light'
            onClick={() => shiftWeek(-1)}
            aria-label='Previous week'
          >
            <i className='fa-solid fa-chevron-left' />
          </button>
          <span className='text-gray-600 fs-7'>
            <Moment format='DD MMM'>{weekStart}</Moment>
            {' - '}
            <Moment format='DD MMM'>
              {new Date(weekStart.getTime() + 6 * 24 * 60 * 60 * 1000)}
            </Moment>
          </span>
          <button
            type='button'
            className='btn btn-sm btn-icon btn-light'
            onClick={() => shiftWeek(1)}
            aria-label='Next week'
          >
            <i className='fa-solid fa-chevron-right' />
          </button>
        </div>

        {/* Scoped to the project so two panels can never share an id and steal each other's
            label clicks. */}
        <div className='form-check form-switch form-check-custom form-check-solid'>
          <input
            className='form-check-input'
            type='checkbox'
            id={`guest-week-weekend-${project.id}`}
            checked={showWeekend}
            onChange={(e) => setShowWeekend(e.target.checked)}
          />
          <label
            className='form-check-label text-gray-600 fs-7'
            htmlFor={`guest-week-weekend-${project.id}`}
          >
            Show weekend
          </label>
        </div>
      </div>

      {error && <div className='alert alert-danger py-3'>{error}</div>}

      {loading ? (
        <div className='text-center text-muted py-5'>Loading your week...</div>
      ) : (
        <div className='d-flex flex-wrap gap-3 justify-content-center justify-content-md-start'>
          {visibleDays.map((day: any) => {
            const date = toDateOnly(new Date(day.date))
            const key = dateKey(day.date)

            const isPast = key < todayKey
            const outsideProject =
              (projectStartKey !== '' && key < projectStartKey) ||
              (projectEndKey !== '' && key > projectEndKey)

            // Locked days can still be read: only the actions are closed off, so the roster
            // below stays reachable for every day in the week.
            const locked = isPast || outsideProject || saving === date

            // A day the project actually runs. isProjectDay comes from the week view and only
            // answers the DaysOfWeek question, so the window check has to come from here too:
            // paging back to a week before the project starts would otherwise mark its
            // weekdays as project days.
            const isProjectDay = !!day.isProjectDay && !outsideProject

            const pickerOpen = picker?.date === date
            const reasons = picker?.state === 'workFromHome' ? offSiteReasons : notWorkingReasons
            const rosterOpen = rosterDate === date

            // Picking on-site resolves to the guest's default site, which is not necessarily one
            // the project runs at. Carry it as an option of its own so the dropdown states where
            // they actually are instead of falling back to a placeholder.
            const siteOptions = projectSites.map((site) => ({
              id: site.siteId,
              name: site.siteName,
            }))

            if (day.mySiteId && !siteOptions.some((option) => option.id === day.mySiteId)) {
              siteOptions.unshift({id: day.mySiteId, name: day.mySiteName || 'Current site'})
            }

            // What this day's existing plan can be switched between without changing state:
            // sites for on-site, the matching reason list for the other two.
            const planOptions =
              day.myState === 'workFromOffice'
                ? siteOptions
                : day.myState === 'workFromHome'
                  ? offSiteReasons
                  : day.myState === 'notWorking'
                    ? notWorkingReasons
                    : []

            const planSelectedId =
              day.myState === 'workFromOffice'
                ? day.mySiteId
                : day.myState === 'workFromHome'
                  ? day.myOffSiteReasonId
                  : day.myNotWorkingReasonId

            const planSelectedOption = planOptions.find((option) => option.id === planSelectedId)
            const planLabel = day.myState === 'workFromOffice' ? 'Site' : 'Reason'

            // An actionable day with nothing chosen yet draws its icons dark, so the three
            // options read as a prompt. Once a day has a plan the unpicked two recede again and
            // let the chosen one stand out, and a locked day stays pale throughout.
            const idleIconColor = !locked && !day.myState ? '#424242' : '#c5c7d1'

            const savePlanOption = (value: string) => {
              if (!value || value === planSelectedId) return

              if (day.myState === 'workFromOffice') save(date, 'workFromOffice', null, value)
              else save(date, day.myState, value)
            }

            return (
              <div
                key={date}
                className='card'
                style={{
                  // Fixed rather than grown to fill the row: with flex-grow the five weekday
                  // cards stretched wider than the seven the weekend toggle adds, so every
                  // toggle resized the whole week. A card is the same size either way now, and
                  // the row wraps instead. maxWidth keeps it inside a phone-width container.
                  width: 219,
                  flexShrink: 0,
                  maxWidth: '100%',
                  // Longhand rather than the `border` shorthand so an invalid theme colour can
                  // only ever discard the colour, never the whole ring.
                  borderWidth: 1,
                  borderStyle: 'solid',
                  borderColor: rosterOpen ? theme.onSite : '#eff2f5',
                  // A thin accent stripe along the top marks the days the project runs on, so
                  // they read apart from the rest of the week at a glance. Declared after the
                  // longhands above so it only overrides the top edge; the other three keep
                  // whatever borderColor chose.
                  ...(isProjectDay ? {borderTopWidth: 6, borderTopColor: theme.onSite} : {}),
                  // Past days are the only ones greyed. A non-project day and a day outside
                  // the project's window both render as normal cards; their actions are
                  // disabled, which is cue enough without dimming the whole card.
                  backgroundColor: 'white',
                  opacity: isPast ? 0.55 : 1,
                }}
              >
                <div className='card-body p-4 text-center'>
                  <div className='fw-bold mb-2 text-dark' style={{fontSize: 13}}>
                    <Moment format='ddd DD MMM'>{day.date}</Moment>
                  </div>

                  {/* Clicking the chart opens that day's on-site roster below the cards. */}
                  <div
                    className='d-flex justify-content-center mb-3'
                    role='button'
                    tabIndex={0}
                    aria-expanded={rosterOpen}
                    aria-label={`Show who is on site on ${date}`}
                    title='Who is on site'
                    style={{cursor: 'pointer'}}
                    onClick={() => setRosterDate(rosterOpen ? null : date)}
                    onKeyDown={(e) => {
                      if (e.key !== 'Enter' && e.key !== ' ') return
                      e.preventDefault()
                      setRosterDate(rosterOpen ? null : date)
                    }}
                  >
                    <DayDoughnut day={day} theme={theme} />
                  </div>

                  <div
                    className='d-flex gap-2'
                    style={{pointerEvents: locked ? 'none' : 'auto'}}
                  >
                    {ACTIONS.map((action) => {
                      const selected = day.myState === action.state
                      const actionColor = action.color(theme)

                      // Says what the second click does, since a chosen action stops being a
                      // way to set the day and becomes the way to take it back.
                      const label = selected ? `Undo ${action.label.toLowerCase()}` : action.label

                      return (
                        <button
                          key={action.state}
                          type='button'
                          className='btn btn-icon btn-sm'
                          title={label}
                          aria-label={label}
                          aria-pressed={selected}
                          disabled={locked}
                          onClick={() => handleAction(day, action.state)}
                          style={{
                            borderWidth: 2,
                            borderStyle: 'solid',
                            borderColor: selected ? actionColor : '#eff2f5',
                            borderRadius: 6,
                            // The two remaining actions split the card evenly instead of each
                            // keeping the fixed width that used to leave room for a third icon.
                            flex: '1 1 0',
                            height: 34,
                          }}
                        >
                          <i
                            className={`${action.icon} fs-4`}
                            style={{color: selected ? actionColor : idleIconColor}}
                          />
                        </button>
                      )
                    })}
                  </div>

                  {/* One select slot per card. The reason picker wins while a day is being moved
                      to off-site or not-working and hasn't been given a reason yet; otherwise a
                      day that already has a plan keeps its own dropdown open, parked on the
                      current choice, so changing site or reason is a single interaction. */}
                  {pickerOpen ? (
                    <div className='mt-3 text-start'>
                      <select
                        className='form-select form-select-sm'
                        defaultValue=''
                        onChange={(e) => {
                          if (!e.target.value) return
                          save(date, picker!.state, e.target.value)
                        }}
                      >
                        <option value=''>Select a reason...</option>
                        {reasons.map((reason) => (
                          <option key={reason.id} value={reason.id}>
                            {reason.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  ) : planOptions.length > 0 ? (
                    <div className='mt-3 text-start'>
                      <select
                        className='form-select form-select-sm'
                        // Controlled off the loaded week rather than defaultValue, so a save
                        // that changes the plan is reflected here without remounting.
                        value={planSelectedOption ? planSelectedId : ''}
                        // A single option is nothing to switch to, so the dropdown has nothing
                        // left to do beyond restating the choice already shown on the card. The
                        // caret is a form-select background-image, so it's dropped the same way.
                        disabled={locked || planOptions.length <= 1}
                        aria-label={planLabel}
                        title={planLabel}
                        onChange={(e) => savePlanOption(e.target.value)}
                        style={
                          planOptions.length <= 1
                            ? {backgroundImage: 'none', paddingRight: '0.75rem'}
                            : undefined
                        }
                      >
                        {/* Only when the stored choice isn't one of the options any more:
                            a reason or site that has since been removed. */}
                        {!planSelectedOption && <option value=''>{planLabel}...</option>}
                        {planOptions.map((option) => (
                          <option key={option.id} value={option.id}>
                            {option.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  ) : null}

                  {/* The guest's own booking(s) out of the project's pool for this day. Only
                      shown on an on-site day: a booking left over from an earlier on-site plan
                      is released by the server the moment the day is switched or undone, so
                      anything still here belongs to the plan above it. */}
                  {day.myState === 'workFromOffice' &&
                    (day.myBookings || []).map((booking: any) => (
                      <div
                        key={booking.id}
                        className='mt-3 rounded bg-light p-2 d-flex align-items-center gap-2 text-start'
                        title={[
                          booking.floorName,
                          booking.assetTypeName,
                          booking.assetName,
                          booking.status,
                        ]
                          .filter(Boolean)
                          .join(' · ')}
                      >
                        <i
                          className='fa-duotone fa-solid fa-objects-column fa-fw'
                          style={{color: theme.onSite}}
                        />

                        <div className='flex-grow-1' style={{minWidth: 0}}>
                          <div className='text-dark fw-bold fs-8 text-truncate'>
                            {booking.assetLetter ? `${booking.assetLetter}: ` : ''}
                            {booking.assetName || '-'}
                          </div>
                          {booking.checkInDate && (
                            <div className='text-gray-600 fs-8 text-truncate'>
                              <Moment utc local format='h:mm A'>
                                {booking.checkInDate}
                              </Moment>
                              {' - '}
                              <Moment utc local format='h:mm A'>
                                {booking.checkOutDue}
                              </Moment>
                            </div>
                          )}
                        </div>

                        <button
                          type='button'
                          className='btn btn-icon btn-sm btn-active-light-danger p-0'
                          style={{width: 20, height: 20}}
                          disabled={locked}
                          title={`Release ${booking.assetName || 'booking'}`}
                          aria-label={`Release ${booking.assetName || 'booking'}`}
                          onClick={() => setToRelease({date, booking})}
                        >
                          <i className='fa-solid fa-xmark text-danger' />
                        </button>
                      </div>
                    ))}

                  {/* Nothing booked yet for an on-site day, so offer the booking outright the
                      way the Teams card does, in the same slot the booking tile would occupy, so
                      the card keeps its height either way. */}
                  {day.myState === 'workFromOffice' && (day.myBookings || []).length === 0 && (
                    <button
                      type='button'
                      className='btn btn-sm btn-light-primary w-100 mt-3 py-2 fs-8 fw-bold'
                      disabled={locked}
                      onClick={() => setBookingDate(date)}
                    >
                      <i className='fa-solid fa-plus fs-8 me-1' />
                      Book {bookableName}
                    </button>
                  )}

                  {saving === date && <div className='text-muted fs-8 mt-2'>Saving...</div>}
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* Only shown when nothing has been picked anywhere in the week yet — once a single day
          has a plan the icons on the cards themselves are explanation enough. Icons reuse the
          day-card action buttons' own bordered-square styling, just always drawn "selected". */}
      {!loading && days.length > 0 && days.every((d: any) => !d.myState) && (
        <div className='d-flex flex-wrap align-items-center gap-4 gap-md-6 mt-4'>
          <div className='d-flex align-items-center gap-2'>
            <div
              className='d-flex flex-center'
              style={{
                borderWidth: 2,
                borderStyle: 'solid',
                borderColor: theme.onSite,
                borderRadius: 6,
                width: 38,
                height: 34,
                flexShrink: 0,
              }}
            >
              <i className='fa-duotone fa-solid fa-building fs-4' style={{color: theme.onSite}} />
            </div>
            <span className='text-gray-600 fs-7'>Click to confirm working on Site</span>
          </div>

          <div className='d-flex align-items-center gap-2'>
            <div
              className='d-flex flex-center'
              style={{
                borderWidth: 2,
                borderStyle: 'solid',
                borderColor: theme.home,
                borderRadius: 6,
                width: 38,
                height: 34,
                flexShrink: 0,
              }}
            >
              <i className='fa-duotone fa-solid fa-house fs-4' style={{color: theme.home}} />
            </div>
            <span className='text-gray-600 fs-7'>
              Click to confirm working off Site{' '}
              <span className='text-gray-600'>- Click again to undo your selection.</span>
            </span>
          </div>
        </div>
      )}

      {bookingDate && (
        <ProjectGuestBookAssetModal
          projectId={project.id}
          date={bookingDate}
          isOpen={!!bookingDate}
          assetTypeName={bookableName}
          onClose={() => setBookingDate(null)}
          onBooked={load}
        />
      )}

      {rosterDate && (
        <div className='card mt-5' style={{borderWidth: 1, borderStyle: 'solid', borderColor: '#eff2f5'}}>
          <div className='card-body p-4'>
            <div className='d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3'>
              <div className='d-flex align-items-center gap-2'>
                <i
                  className='fa-duotone fa-solid fa-user fs-3'
                  style={{color: theme.onSite}}
                />
                <span className='fw-bold text-dark'>
                  On site <Moment format='ddd DD MMM'>{rosterDay?.date || rosterDate}</Moment>
                </span>
                {!rosterLoading && (
                  <span className='badge badge-light fw-bold'>{roster.length}</span>
                )}
              </div>

              <button
                type='button'
                className='btn btn-sm btn-icon btn-light'
                aria-label='Close on-site list'
                onClick={() => setRosterDate(null)}
              >
                <i className='fa-solid fa-xmark' />
              </button>
            </div>

            {rosterLoading ? (
              <div className='text-muted fs-7'>Loading...</div>
            ) : roster.length === 0 ? (
              <div className='text-muted fs-7'>Nobody is on site for this day.</div>
            ) : (
              <div className='row g-3'>
                {roster.map((entry: any) => (
                  <div className='col-12 col-sm-6 col-lg-4' key={entry.userId}>
                    <div className='d-flex align-items-center gap-3'>
                      <div
                        className='d-flex flex-center rounded-circle fw-bold text-white'
                        style={{
                          width: 32,
                          height: 32,
                          backgroundColor: theme.onSite,
                          flexShrink: 0,
                        }}
                        aria-hidden='true'
                      >
                        {(entry.userName || '?').charAt(0).toUpperCase()}
                      </div>
                      <div style={{minWidth: 0}}>
                        <div className='text-dark fw-bold fs-7 text-truncate'>
                          {entry.userName || '-'}
                        </div>
                        {entry.siteName && (
                          <div className='text-gray-600 fs-8 text-truncate'>{entry.siteName}</div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
